/**
 * StructuredEditor — renders a Section content object as a per-field-type form
 * driven by a schema (see `sectionSchemas.js`).
 *
 * Props:
 *   schema     { title, fields: [...] }
 *   value      current content object
 *   onChange   (nextValue) => void
 *   onPickAsset(onPicked) => opens the media picker; parent supplies the
 *              search UI and calls `onPicked(assetKey)` when the admin
 *              selects a media item. Keeps this component free of network I/O.
 */
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { ChevronDown, ChevronRight, Plus, Trash2, Image as ImageIcon, MoveUp, MoveDown } from 'lucide-react';
import { buildBlankValue } from './sectionSchemas';

// ── Primitive field renderer ──
const FieldRow = ({ field, value, onChange, onPickAsset, testIdPrefix }) => {
  const testId = `${testIdPrefix}-${field.key}`;
  const commonLabel = (
    <label className="block text-xs font-semibold text-slate-600 mb-1">
      {field.label}
      {field.help && <span className="ml-1 text-slate-400 font-normal">— {field.help}</span>}
    </label>
  );

  switch (field.type) {
    case 'textarea':
      return (
        <div>
          {commonLabel}
          <Textarea
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm"
            rows={3}
            data-testid={testId}
          />
        </div>
      );
    case 'number':
      return (
        <div>
          {commonLabel}
          <Input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            className="text-sm"
            data-testid={testId}
          />
        </div>
      );
    case 'boolean':
      return (
        <div className="flex items-center justify-between py-1">
          <label className="text-xs font-semibold text-slate-600">{field.label}</label>
          <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
        </div>
      );
    case 'select':
      return (
        <div>
          {commonLabel}
          <Select value={value ?? ''} onValueChange={onChange}>
            <SelectTrigger className="text-sm" data-testid={testId}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case 'asset_key':
      return (
        <div>
          {commonLabel}
          <div className="flex gap-2">
            <Input
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              className="text-sm font-mono"
              placeholder="asset_key"
              data-testid={testId}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onPickAsset((key) => onChange(key))}
              data-testid={`${testId}-pick`}
            >
              <ImageIcon className="w-4 h-4 mr-1" /> Pick
            </Button>
          </div>
        </div>
      );
    case 'url':
    case 'href':
    case 'text':
    default:
      return (
        <div>
          {commonLabel}
          <Input
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm"
            data-testid={testId}
          />
        </div>
      );
  }
};

// ── Nested object renderer ──
const ObjectFields = ({ fields, value, onChange, onPickAsset, testIdPrefix }) => {
  const setKey = (key, next) => onChange({ ...(value || {}), [key]: next });
  return (
    <div className="space-y-3">
      {fields.map((f) => (
        <FieldNode
          key={f.key}
          field={f}
          value={value?.[f.key]}
          onChange={(v) => setKey(f.key, v)}
          onPickAsset={onPickAsset}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
};

// ── Array (repeating group) renderer ──
const ArrayField = ({ field, value, onChange, onPickAsset, testIdPrefix }) => {
  const items = Array.isArray(value) ? value : [];
  const [expanded, setExpanded] = useState(() => new Set(items.map((_, i) => i)));

  const toggle = (i) => {
    const next = new Set(expanded);
    if (next.has(i)) next.delete(i); else next.add(i);
    setExpanded(next);
  };

  const update = (i, next) => {
    const arr = [...items];
    arr[i] = next;
    onChange(arr);
  };
  const remove = (i) => {
    const arr = items.filter((_, idx) => idx !== i);
    onChange(arr);
    const next = new Set(); expanded.forEach((v) => { if (v < i) next.add(v); else if (v > i) next.add(v - 1); });
    setExpanded(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const add = () => {
    const blank = {};
    for (const f of field.fields || []) blank[f.key] = buildBlankValue(f);
    onChange([...items, blank]);
    setExpanded(new Set([...expanded, items.length]));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-600">
          {field.label} <span className="text-slate-400 font-normal">({items.length})</span>
        </label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testIdPrefix}-${field.key}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => {
          const isOpen = expanded.has(i);
          const label = field.itemLabel ? field.itemLabel(item, i) : `Item ${i + 1}`;
          return (
            <div key={i} className="border rounded-md" data-testid={`${testIdPrefix}-${field.key}-item-${i}`}>
              <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 border-b">
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="flex-1 flex items-center gap-1 text-left text-sm font-medium"
                  data-testid={`${testIdPrefix}-${field.key}-toggle-${i}`}
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  <span className="truncate">{label}</span>
                </button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => move(i, -1)} disabled={i === 0}
                  data-testid={`${testIdPrefix}-${field.key}-up-${i}`}>
                  <MoveUp className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => move(i, +1)} disabled={i === items.length - 1}
                  data-testid={`${testIdPrefix}-${field.key}-down-${i}`}>
                  <MoveDown className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600"
                  onClick={() => remove(i)}
                  data-testid={`${testIdPrefix}-${field.key}-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {isOpen && (
                <div className="p-3">
                  <ObjectFields
                    fields={field.fields || []}
                    value={item}
                    onChange={(v) => update(i, v)}
                    onPickAsset={onPickAsset}
                    testIdPrefix={`${testIdPrefix}-${field.key}-${i}`}
                  />
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-xs text-slate-400 italic py-2">No items. Click &quot;Add&quot; to create the first one.</p>
        )}
      </div>
    </div>
  );
};

// ── Router: pick renderer by field.type ──
const FieldNode = ({ field, value, onChange, onPickAsset, testIdPrefix }) => {
  if (field.type === 'object') {
    return (
      <div className="border-l-2 border-slate-200 pl-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{field.label}</p>
        <ObjectFields
          fields={field.fields || []}
          value={value || {}}
          onChange={onChange}
          onPickAsset={onPickAsset}
          testIdPrefix={`${testIdPrefix}-${field.key}`}
        />
      </div>
    );
  }
  if (field.type === 'array') {
    return (
      <ArrayField
        field={field}
        value={value}
        onChange={onChange}
        onPickAsset={onPickAsset}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  return (
    <FieldRow
      field={field}
      value={value}
      onChange={onChange}
      onPickAsset={onPickAsset}
      testIdPrefix={testIdPrefix}
    />
  );
};

const StructuredEditor = ({ schema, value, onChange, onPickAsset }) => {
  const setKey = (key, next) => onChange({ ...(value || {}), [key]: next });
  return (
    <div className="space-y-4" data-testid="structured-editor">
      {schema.title && (
        <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">{schema.title}</p>
      )}
      {schema.fields.map((f) => (
        <FieldNode
          key={f.key}
          field={f}
          value={value?.[f.key]}
          onChange={(v) => setKey(f.key, v)}
          onPickAsset={onPickAsset}
          testIdPrefix={`sf`}
        />
      ))}
    </div>
  );
};

export default StructuredEditor;
